import { useEffect, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { PageHeading } from '@/components/Layout.tsx'
import { LeafDivider, Sprig } from '@/components/Botanical.tsx'
import { Plus } from '@/components/Icons.tsx'
import { api, ApiError } from '@/lib/api.ts'
import type { Notebook } from '@/lib/types.ts'

export default function Notebooks() {
  const [notebooks, setNotebooks] = useState<Notebook[]>([])
  const [loading, setLoading] = useState(true)
  const [composing, setComposing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api.notebooks
      .list()
      .then(({ notebooks }) => setNotebooks(notebooks))
      .catch(() => setError('The notebooks could not be opened.'))
      .finally(() => setLoading(false))
  }, [])

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    const form = event.currentTarget
    const data = new FormData(form)
    try {
      const { notebook } = await api.notebooks.create(
        String(data.get('title') ?? ''),
        String(data.get('prompt') ?? ''),
      )
      setNotebooks((prev) => [...prev, notebook])
      setComposing(false)
      form.reset()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not save.')
    }
  }

  return (
    <>
      <PageHeading eyebrow="Between the two of you" title="Notebooks">
        Nobody else can read these. Write badly, write half a thought, come back to it later.
      </PageHeading>

      {error && <p className="mb-6 text-[0.88rem] text-terracotta">{error}</p>}
      {loading && <p className="text-whisper">Opening…</p>}

      <ul className="divide-y divide-linen border-y border-linen">
        {notebooks.map((nb, i) => (
          <li key={nb.id} className="animate-unfurl" style={{ animationDelay: `${i * 60}ms` }}>
            <Link to={`/notebooks/${nb.id}`} className="group flex items-center gap-6 py-7">
              <span className="w-8 shrink-0 font-display text-[0.9rem] text-whisper transition-colors group-hover:text-sage">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span className="min-w-0 flex-1">
                <span className="block font-display text-[1.5rem] leading-tight transition-colors duration-300 group-hover:text-fern">
                  {nb.title}
                </span>
                {nb.prompt && <span className="prose-soft mt-1.5 block text-[0.92rem]">{nb.prompt}</span>}
              </span>
              <span className="shrink-0 text-sage opacity-0 transition-all duration-300 ease-botanical group-hover:translate-x-1 group-hover:opacity-100">
                →
              </span>
            </Link>
          </li>
        ))}
      </ul>

      <div className="mt-10">
        {composing ? (
          <form onSubmit={create} className="sheet max-w-measure space-y-3.5 p-6 animate-unfurl">
            <label className="block">
              <span className="eyebrow">Title</span>
              <input name="title" required autoFocus placeholder="What is this one for?" className="field mt-1.5" />
            </label>
            <label className="block">
              <span className="eyebrow">A line underneath (optional)</span>
              <input name="prompt" placeholder="A note to your future selves" className="field mt-1.5" />
            </label>
            <div className="flex gap-2 pt-1">
              <button type="submit" className="btn-primary">Make it</button>
              <button type="button" onClick={() => setComposing(false)} className="btn-ghost">Never mind</button>
            </div>
          </form>
        ) : (
          <button onClick={() => setComposing(true)} className="btn-ghost">
            <Plus className="h-4 w-4" />
            Start another notebook
          </button>
        )}
      </div>

      <div className="mt-20 flex justify-center opacity-40">
        <Sprig tone="var(--c-sage)" className="h-24 w-auto animate-sway" />
      </div>
      <LeafDivider tone="var(--c-clay)" className="mx-auto mt-6 h-4 w-48 opacity-30" />
    </>
  )
}
